import { ReactElement } from 'react';

import {
   ConfidentialityMode,
   PractitionerUpdateConfidentialityModeCommand,
} from '@dreryk/egabinet-personnel-frontend-api';
import { PractitionerId } from '@dreryk/egabinet-personnel-published-language-api';
import { VisitUpdateAndPropagateConfidentialityLevelCommand } from '@dreryk/egabinet-visit-frontend-api';

import { useCqrsCommand } from '@dreryk/command-query-react-common';
import { commonTranslationKeys, PromiseVoidFunction } from '@dreryk/react-common';
import { dispatchSnackbarPositiveEvent, ModalConfirmButton } from '@dreryk/react-components';

import { useGetCurrentPractitionerGlobalState, useRefetchCurrentPractitioner } from '~libs.shared.account';

import { practitionersTranslationKeys } from '../../../../../../../translations';
import { DefaultConfidentialityModeUpdateFormController } from '../form/DefaultConfidentialityModeUpdateForm.types';

type DefaultConfidentialityModeHistoricalUpdateModalConfirmButtonProps = Pick<
   DefaultConfidentialityModeUpdateFormController,
   'handleSubmit'
> & {
   readonly mode: ConfidentialityMode;
   readonly practitionerId: number;
   readonly onSuccess: VoidFunction;
};

export const DefaultConfidentialityModeHistoricalUpdateModalConfirmButton = (
   props: DefaultConfidentialityModeHistoricalUpdateModalConfirmButtonProps
): ReactElement => {
   const currentPractitioner = useGetCurrentPractitionerGlobalState();
   const refetchCurrentPractitioner = useRefetchCurrentPractitioner();

   const { executeAsync: updateConfidentialityMode } = useCqrsCommand<void, void>(() => {
      const command = new PractitionerUpdateConfidentialityModeCommand();
      command.id = PractitionerId.of(props.practitionerId);
      command.mode = props.mode;
      return command;
   });

   const { executeAsync: propagateConfidentialityLevel } = useCqrsCommand<void, void>(() => {
      const command = new VisitUpdateAndPropagateConfidentialityLevelCommand();
      command.practitionerId = PractitionerId.of(props.practitionerId);
      command.confidentialityLevel = props.mode;
      return command;
   });

   const updateConfidentialityModeWithHistory: PromiseVoidFunction = async () => {
      await Promise.all([updateConfidentialityMode(), propagateConfidentialityLevel()]);
      dispatchSnackbarPositiveEvent(
         practitionersTranslationKeys.defaultConfidentialResourceModeUpdate.savedWithHistorical
      );

      if (currentPractitioner.id === props.practitionerId) {
         refetchCurrentPractitioner();
      }

      props.onSuccess();
   };

   return (
      <ModalConfirmButton
         label={commonTranslationKeys.save}
         onClick={props.handleSubmit(updateConfidentialityModeWithHistory)}
      />
   );
};
